import {
  Add as AddIcon,
  Assessment as AssessmentIcon,
  Assignment as AssignmentIcon,
  AttachMoney as AttachMoneyIcon,
  BarChart as BarChartIcon,
  Category as CategoryIcon,
  Dashboard as DashboardIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Label as LabelIcon,
  LocalOffer as LocalOfferIcon,
  LocalShipping as LocalShippingIcon,
  NotificationsActive as NotificationsActiveIcon,
  Payment as PaymentIcon,
  People as PeopleIcon,
  PieChart as PieChartIcon,
  RateReview as RateReviewIcon,
  Receipt as ReceiptIcon,
  RequestPage as RequestPageIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Share as ShareIcon,
  Storage as StorageIcon,
  Store as StoreIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  subItems?: { text: string; icon: React.ReactElement; path: string }[];
  hasChevron?: boolean;
  isNew?: boolean;
  badge?: number;
}

interface MenuSection {
  section: string;
  items: MenuItem[];
}

interface AdminSidebarProps {
  open: boolean;
  onProfileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
}

const drawerWidth = 280;

const menuItems: MenuSection[] = [
  {
    section: "admin.sidebar.sectionDashboard",
    items: [
      {
        text: "admin.sidebar.dashboard",
        icon: <DashboardIcon />,
        path: "/admin",
        subItems: [],
      },
    ],
  },
  {
    section: "admin.sidebar.sectionAnalytics",
    items: [
      {
        text: "admin.sidebar.analytics",
        icon: <AssessmentIcon />,
        path: "/admin/analytics",
        subItems: [
          {
            text: "admin.sidebar.salesOverview",
            icon: <TrendingUpIcon />,
            path: "/admin/analytics/sales",
          },
          {
            text: "admin.sidebar.customerInsights",
            icon: <BarChartIcon />,
            path: "/admin/analytics/customers",
          },
          {
            text: "admin.sidebar.productPerformance",
            icon: <PieChartIcon />,
            path: "/admin/analytics/products",
          },
          {
            text: "admin.sidebar.revenueTimeline",
            icon: <TimelineIcon />,
            path: "/admin/analytics/revenue",
          },
          {
            text: "admin.sidebar.promoAnalytics",
            icon: <LocalOfferIcon />,
            path: "/admin/promotion-codes/analytics",
          },
          {
            text: "admin.sidebar.zincApiUsage",
            icon: <StorageIcon />,
            path: "/admin/analytics/zinc-usage",
          },
        ],
        hasChevron: true,
      },
    ],
  },
  {
    section: "admin.sidebar.sectionOrders",
    items: [
      {
        text: "admin.sidebar.ordersManagement",
        icon: <ReceiptIcon />,
        path: "",
        subItems: [
          {
            text: "admin.sidebar.orders",
            icon: <ReceiptIcon />,
            path: "/admin/orders",
          },
          {
            text: "admin.sidebar.items",
            icon: <CategoryIcon />,
            path: "/admin/items",
          },
          {
            text: "admin.sidebar.orderStatuses",
            icon: <AssignmentIcon />,
            path: "/admin/order-statuses",
          },
          {
            text: "admin.sidebar.itemStatuses",
            icon: <AssignmentIcon />,
            path: "/admin/item-statuses",
          },
          {
            text: "admin.sidebar.customerReviews",
            icon: <RateReviewIcon />,
            path: "/admin/reviews",
          },
          {
            text: "admin.sidebar.reports",
            icon: <AssessmentIcon />,
            path: "/admin/reports",
          },
        ],
        hasChevron: true,
        badge: 12,
      },
    ],
  },
  {
    section: "admin.sidebar.sectionShipping",
    items: [
      {
        text: "admin.sidebar.shippingManagement",
        icon: <LocalShippingIcon />,
        path: "",
        subItems: [
          {
            text: "admin.sidebar.shippingMethods",
            icon: <LocalShippingIcon />,
            path: "/admin/shipping",
          },
          {
            text: "admin.sidebar.pricing",
            icon: <AttachMoneyIcon />,
            path: "/admin/pricing",
          },
          {
            text: "admin.sidebar.productTypeKeywords",
            icon: <LabelIcon />,
            path: "/admin/pricing/product-type-keywords",
          },
          {
            text: "admin.sidebar.tracking",
            icon: <LocalShippingIcon />,
            path: "/admin/shipping/tracking",
          },
          {
            text: "admin.sidebar.taxesAndFees",
            icon: <RequestPageIcon />,
            path: "/admin/taxes-fees",
          },
          {
            text: "admin.sidebar.promotionCodes",
            icon: <LocalOfferIcon />,
            path: "/admin/promotion-codes",
          },
          {
            text: "admin.sidebar.referralSettings",
            icon: <ShareIcon />,
            path: "/admin/referral-settings",
          },
          {
            text: "admin.sidebar.referralAnalytics",
            icon: <ShareIcon />,
            path: "/admin/referral-analytics",
          },
          {
            text: "admin.sidebar.exchangeRates",
            icon: <AttachMoneyIcon />,
            path: "/admin/exchange-rates",
          },
          {
            text: "admin.sidebar.cargoCompanies",
            icon: <LocalShippingIcon />,
            path: "/admin/cargo-companies",
          },
        ],
        hasChevron: true,
      },
    ],
  },
  {
    section: "admin.sidebar.sectionPayments",
    items: [
      {
        text: "admin.sidebar.payments",
        icon: <PaymentIcon />,
        path: "/admin/payments",
        subItems: [
          {
            text: "admin.sidebar.paymentMethods",
            icon: <PaymentIcon />,
            path: "/admin/payments/methods",
          },
          {
            text: "admin.sidebar.paymentMethodDiscounts",
            icon: <LocalOfferIcon />,
            path: "/admin/payment-method-discounts",
          },
          {
            text: "admin.sidebar.transactions",
            icon: <ReceiptIcon />,
            path: "/admin/payments/transactions",
          },
        ],
        hasChevron: true,
      },
    ],
  },
  {
    section: "admin.sidebar.sectionInvoices",
    items: [
      {
        text: "admin.sidebar.invoices",
        icon: <ReceiptIcon />,
        path: "/admin/invoices",
        subItems: [
          {
            text: "admin.sidebar.allInvoices",
            icon: <ReceiptIcon />,
            path: "/admin/invoices/list",
          },
          {
            text: "admin.sidebar.createInvoice",
            icon: <AddIcon />,
            path: "/admin/invoices/create",
          },
        ],
        hasChevron: true,
      },
    ],
  },
  {
    section: "admin.sidebar.sectionUserManagement",
    items: [
      {
        text: "admin.sidebar.userManagement",
        icon: <PeopleIcon />,
        path: "",
        subItems: [
          {
            text: "admin.sidebar.users",
            icon: <PeopleIcon />,
            path: "/admin/users",
          },
          {
            text: "admin.sidebar.roles",
            icon: <SecurityIcon />,
            path: "/admin/roles",
          },
        ],
        hasChevron: true,
      },
    ],
  },
  {
    section: "admin.sidebar.sectionSystem",
    items: [
      {
        text: "admin.sidebar.cacheAndDebug",
        icon: <StorageIcon />,
        path: "",
        subItems: [
          {
            text: "admin.sidebar.productCacheDb",
            icon: <StorageIcon />,
            path: "/admin/product-cache",
          },
          {
            text: "admin.sidebar.shippingMetadata",
            icon: <LocalShippingIcon />,
            path: "/admin/product-shipping-metadata",
          },
          {
            text: "admin.sidebar.redisCache",
            icon: <StorageIcon />,
            path: "/admin/redis-cache",
          },
        ],
        hasChevron: true,
      },
    ],
  },
  {
    section: "admin.sidebar.sectionSettings",
    items: [
      {
        text: "admin.sidebar.settings",
        icon: <SettingsIcon />,
        path: "/admin/settings",
        subItems: [
          {
            text: "admin.sidebar.general",
            icon: <SettingsIcon />,
            path: "/admin/settings/general",
          },
          {
            text: "admin.sidebar.storeInfo",
            icon: <StoreIcon />,
            path: "/admin/settings/store",
          },
          {
            text: "admin.sidebar.notifications",
            icon: <NotificationsActiveIcon />,
            path: "/admin/settings/notifications",
          },
        ],
        hasChevron: true,
      },
    ],
  },
];

const AdminSidebar: React.FC<AdminSidebarProps> = React.memo(
  ({ open, onProfileMenuOpen }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const isRestoringScroll = useRef(false);

    // Handle expanding/collapsing menu items
    const handleExpandItem = useCallback((itemText: string) => {
      setExpandedItems((prev) =>
        prev.includes(itemText)
          ? prev.filter((item) => item !== itemText)
          : [...prev, itemText]
      );
    }, []);

    // Check if an item is active (for highlighting)
    const isItemActive = useCallback(
      (
        path: string,
        subItems?: { text: string; icon: React.ReactElement; path: string }[]
      ) => {
        if (!path) {
          // For parent items without direct paths, check if any child is active
          return (
            subItems?.some(
              (subItem) =>
                location.pathname === subItem.path ||
                location.pathname.startsWith(subItem.path + "/")
            ) || false
          );
        }
        return location.pathname === path;
      },
      [location.pathname]
    );

    // Save scroll position to localStorage
    const saveScrollPosition = useCallback(() => {
      if (scrollContainerRef.current && !isRestoringScroll.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        localStorage.setItem("sidebar-scroll-position", scrollTop.toString());
      }
    }, []);

    // Restore scroll position from localStorage
    const restoreScrollPosition = useCallback(() => {
      if (scrollContainerRef.current) {
        const savedPosition = localStorage.getItem("sidebar-scroll-position");
        if (savedPosition) {
          isRestoringScroll.current = true;
          scrollContainerRef.current.scrollTop = parseInt(savedPosition, 10);
          // Reset flag after a short delay
          setTimeout(() => {
            isRestoringScroll.current = false;
          }, 100);
        }
      }
    }, []);

    // Handle navigation - save scroll position before navigating
    const handleNavigation = useCallback(
      (path: string) => {
        if (path) {
          saveScrollPosition();
          navigate(path);
        }
      },
      [navigate, saveScrollPosition]
    );

    // Auto-expand parent sections when navigating to child items
    useEffect(() => {
      const currentPath = location.pathname;
      const newExpandedItems = new Set(expandedItems);

      // Find parent sections that contain the current path
      menuItems.forEach((section) => {
        section.items.forEach((item) => {
          if (item.subItems?.some((subItem) => subItem.path === currentPath)) {
            newExpandedItems.add(item.text);
          }
        });
      });

      // Update expanded items if needed
      if (
        newExpandedItems.size !== expandedItems.length ||
        !Array.from(newExpandedItems).every((item) =>
          expandedItems.includes(item)
        )
      ) {
        setExpandedItems(Array.from(newExpandedItems));
      }
    }, [location.pathname, expandedItems]);

    // Restore scroll position on mount and route change
    useEffect(() => {
      const timer = setTimeout(() => {
        restoreScrollPosition();
      }, 50);
      return () => clearTimeout(timer);
    }, [location.pathname, restoreScrollPosition]);

    // Save scroll position on scroll
    useEffect(() => {
      const handleScroll = () => {
        saveScrollPosition();
      };

      const scrollElement = scrollContainerRef.current;
      if (scrollElement) {
        scrollElement.addEventListener("scroll", handleScroll, {
          passive: true,
        });
        return () => scrollElement.removeEventListener("scroll", handleScroll);
      }
    }, [saveScrollPosition]);

    // Save scroll position before page unload
    useEffect(() => {
      const handleBeforeUnload = () => {
        saveScrollPosition();
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [saveScrollPosition]);

    if (!open) return null;

    return (
      <Drawer
        variant="permanent"
        open={true}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: "#ffffff",
            borderRight: "1px solid #e5e7eb",
            borderRadius: 0,
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            zIndex: 1200,
            transform: "translateZ(0)", // Force hardware acceleration
            willChange: "auto", // Prevent layout thrashing
          },
        }}
      >
        {/* Brand Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e5e7eb",
            background: "#ffffff",
            display: "flex",
            alignItems: "center",
            minHeight: "80px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              width: "100%",
            }}
          >
            <img
              src="/logos/logo-png.png"
              alt="R11 Logo"
              style={{
                height: "45px",
                width: "auto",
                objectFit: "contain",
              }}
            />

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                color="#111827"
                sx={{ mb: 0.5, fontSize: "18px" }}
              >
                US2TJK
              </Typography>
              <Typography
                variant="body2"
                color="#6b7280"
                fontWeight={500}
                fontSize="12px"
              >
                {t("admin.sidebar.adminDashboard")}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Search Bar */}
        {/* <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: '#f8fafc',
            borderRadius: '8px',
            px: 3,
            py: 2,
            border: '1px solid #e5e7eb',
            '&:hover': {
              borderColor: '#6366f1',
            },
            '&:focus-within': {
              borderColor: '#6366f1',
              boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
            }
          }}
        >
          <SearchIcon sx={{ color: '#6b7280', mr: 2, fontSize: 20 }} />
          <InputBase
            placeholder="Search menu..."
            sx={{ 
              flex: 1, 
              fontSize: '14px', 
              fontWeight: 500,
              '& input': {
                padding: 0,
              }
            }}
          />
        </Box>
      </Box> */}

        {/* Navigation Menu */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flexGrow: 1,
            overflow: "auto",
            height: "calc(100vh - 200px)", // Fixed height to prevent overflow
            position: "relative",
            transform: "translateZ(0)", // Force hardware acceleration
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          }}
        >
          {menuItems.map((section) => (
            <Box key={section.section}>
              {/* Section Header */}
              <Box sx={{ px: 3, py: 2, mt: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "1.2px",
                    color: "#9ca3af",
                    mb: 1,
                  }}
                >
                  {t(section.section)}
                </Typography>
              </Box>

              {/* Section Items */}
              <List sx={{ px: 3, py: 0 }}>
                {section.items.map((item) => (
                  <Box key={item.text}>
                    <ListItemButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (item.subItems && item.subItems.length > 0) {
                          handleExpandItem(item.text);
                        } else {
                          handleNavigation(item.path);
                        }
                      }}
                      selected={isItemActive(item.path, item.subItems)}
                      sx={{
                        borderRadius: "8px",
                        mb: 1,
                        py: 1.5,
                        px: 2,
                        minHeight: "48px",
                        "&.Mui-selected": {
                          background: "#6366f1",
                          color: "white",
                          "&:hover": {
                            background: "#4f46e5",
                          },
                          "& .MuiListItemIcon-root": {
                            color: "white",
                          },
                        },
                        "&:hover": {
                          background: "#f8fafc",
                          transform: "translateX(2px)",
                          transition: "all 0.2s ease",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 44, color: "#6b7280" }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={t(item.text)}
                        primaryTypographyProps={{
                          fontWeight: isItemActive(item.path, item.subItems)
                            ? 600
                            : 500,
                          fontSize: "15px",
                          color: isItemActive(item.path, item.subItems)
                            ? "inherit"
                            : "#374151",
                        }}
                      />
                      {item.badge && (
                        <Chip
                          label={item.badge}
                          size="small"
                          color="error"
                          sx={{
                            height: 20,
                            fontSize: "10px",
                            fontWeight: 600,
                            background: "#ef4444",
                            color: "white",
                          }}
                        />
                      )}
                      {item.hasChevron &&
                        (expandedItems.includes(item.text) ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        ))}
                    </ListItemButton>

                    {/* Sub Items */}
                    {item.subItems && item.subItems.length > 0 && (
                      <Collapse
                        in={expandedItems.includes(item.text)}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List component="div" disablePadding>
                          {item.subItems?.map((subItem) => (
                            <ListItemButton
                              key={subItem.text}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleNavigation(subItem.path);
                              }}
                              selected={isItemActive(subItem.path, [])}
                              sx={{
                                pl: 4,
                                borderRadius: 0,
                                mb: 0.5,
                                "&.Mui-selected": {
                                  backgroundColor: "#dbeafe",
                                  color: "#2563eb",
                                  borderLeft: "3px solid #2563eb",
                                  "&:hover": {
                                    backgroundColor: "#bfdbfe",
                                  },
                                  "& .MuiListItemIcon-root": {
                                    color: "#2563eb",
                                  },
                                },
                                "&:hover": {
                                  backgroundColor: "#f8fafc",
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={t(subItem.text)}
                                primaryTypographyProps={{
                                  fontSize: "13px",
                                  fontWeight: isItemActive(subItem.path, [])
                                    ? 600
                                    : 500,
                                }}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </Box>
                ))}
              </List>
            </Box>
          ))}
        </Box>

        {/* User Profile Section */}
        <Box
          sx={{
            p: 0,
            border: "none",
            background: "#f8fafc",
            borderRadius: "0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              py: 2,
              px: 2,
              borderRadius: "8px",
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
              "&:hover": {
                background: "#f9fafb",
              },
            }}
            onClick={onProfileMenuOpen}
          >
            <Avatar
              sx={{
                width: 42,
                height: 42,
                background: "#6366f1",
                fontWeight: 700,
                fontSize: "16px",
              }}
            >
              A
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                {t("admin.sidebar.adminUser")}
              </Typography>
              <Typography
                variant="body2"
                color="#6b7280"
                fontSize="13px"
                fontWeight={500}
              >
                {t("admin.sidebar.storeManager")}
              </Typography>
            </Box>
            <KeyboardArrowRightIcon sx={{ color: "#6b7280", fontSize: 20 }} />
          </Box>
        </Box>
      </Drawer>
    );
  }
);

// Custom comparison function to prevent unnecessary re-renders
AdminSidebar.displayName = "AdminSidebar";

export default AdminSidebar;
